/**
 * Traceability for imported heats and lots — typed wrappers over the B8a reads (platform-core/internal/twin/
 * traceability_reads.go) and the observation layer's lot list:
 *
 *   GET /v1/twin/observations?grain=lot     every casting lot, newest booked day first (a page of them)
 *   GET /v1/twin/lots/{lot_id}/outcome      a lot's outcome as booked, and its heats with their ladle chemistry
 *   GET /v1/twin/batches/{heat_id}/lots     the lots a heat fed: its own, and the ones it shared with other heats
 *
 * A plant that books scrap per production day and part (an imported CMS) has no serialised units, so these are how its
 * people walk heat → lots → defects and back (the CWC demo walk, 2026-09-26).
 */

import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";
import { qs } from "./qs";
import type { CastingHoldBadge } from "./casting-holds";

const isLiveApiFlag = import.meta.env.VITE_USE_LIVE_API === "true";

function useLiveEnabled(): boolean {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  return isLiveApiFlag && isAuthenticated && activeOrgId !== null;
}

/** One lot observation (observations.go observationDTO), the fields the lot list reads. */
export interface LotObservation {
  id: string;
  grain: string;
  source: string;
  /** The day the plant BOOKED the lot (YYYY-MM-DD). */
  plant_day: string;
  part?: string;
  product_id?: string;
  grade?: string;
  /** The shift and line the lot was booked on, for a plant that books its lots per shift and line; absent otherwise. */
  shift?: string;
  line?: string;
  /** Pieces; null = not known. */
  cast: number | null;
  scrap: number | null;
  scrap_rate: number | null;
  /** Scrapped pieces per defect as booked (canonical names). */
  defects: Record<string, number> | null;
  /** twin_batches ids of the heats that fed it. */
  heats: string[] | null;
  outcome_only: boolean;
  /** The casting holds its heats are in, open first (step 6); absent on a bucket and from an older API. */
  casting_holds?: CastingHoldBadge[];
}

export interface LotObservationPage {
  items: LotObservation[];
  total: number;
  limit: number;
  offset: number;
}

/** A heat parameter as its ladles measured it (summarizeLadles): n ladles, their mean and span. */
export interface HeatChemParam {
  param: string;
  unit: string;
  n: number;
  mean: number;
  min: number;
  max: number;
  provenance: string;
}

export interface LotOutcomeHeat {
  id: string;
  heat_number: string;
  started_at: string;
  finished_at?: string;
  chemistry: HeatChemParam[];
}

export interface LotOutcome {
  lot: {
    id: string;
    lot_number: string;
    material_class: string;
    part?: string;
    day?: string;
    grade?: string;
    /** The shift and line it was booked on, where the plant books its lots per shift and line. */
    shift?: string;
    line?: string;
    product_id?: string;
    measured_at?: string | null;
  };
  outcome: {
    cast: number | null;
    scrap: number | null;
    defects: Record<string, number> | null;
    scrap_rate?: number;
    /**
     * Floor rejects logged by part and cast date attached to the lot as examples (D1): never in its scrap or defects;
     * listed with `rejectCapture.lotRecords`. Absent from an older API.
     */
    floor_examples?: number;
  };
  heats: LotOutcomeHeat[];
  note?: string;
}

/** A lot a heat fed (heatLotRow): shared_with names the other heats that fed it. */
export interface HeatLot {
  lot_id: string;
  lot_number: string;
  day?: string;
  part?: string;
  /** The shift and line it was booked on, where the plant books its lots per shift and line. */
  shift?: string;
  line?: string;
  product_id?: string;
  cast: number;
  /** -1 = not recorded. */
  scrap: number;
  /** The plant's sheet left its scrap blank: a lot booked with its castings only. Its scrap is -1. */
  scrap_unknown?: boolean;
  /** The defects seen on it with no piece count: never pieces. */
  flags?: string[];
  defects: Record<string, number>;
  shared_with: string[];
}

export interface HeatLots {
  lots: HeatLot[];
  /** Castings and scrap of the lots this heat fed ALONE: its own. */
  attributed_cast: number;
  attributed_scrap: number;
  /** Lots poured from this heat and others: their castings cannot be split by heat. */
  shared_lots: number;
  shared_cast: number;
  shared_scrap: number;
  /** Lots whose scrap was not recorded: in `lots` with their castings, in none of the sums above. */
  scrap_unknown_lots?: number;
  scrap_unknown_cast?: number;
  note?: string;
}

export const twinTraceability = {
  lots: (params: { limit?: number; offset?: number; from?: string; to?: string; part?: string } = {}) =>
    api.get<LotObservationPage>(`/v1/twin/observations${qs({ grain: "lot", ...params })}`),
  lotOutcome: (lotId: string) => api.get<LotOutcome>(`/v1/twin/lots/${encodeURIComponent(lotId)}/outcome`),
  heatLots: (heatId: string) => api.get<HeatLots>(`/v1/twin/batches/${encodeURIComponent(heatId)}/lots`),
};

/** A window of booked plant days (YYYY-MM-DD, both inclusive), as the observations read a date: a plant day. */
export interface LotDays {
  from: string;
  to: string;
}

export function useLotObservationsQuery(page: number, pageSize: number, enabled = true, days?: LotDays | null) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: ["twin-lot-observations", page, pageSize, days?.from ?? "", days?.to ?? ""],
    queryFn: () =>
      twinTraceability.lots({ limit: pageSize, offset: page * pageSize, from: days?.from, to: days?.to }),
    enabled: live && enabled,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

export function useLotOutcomeQuery(lotId: string | undefined) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: ["twin-lot-outcome", lotId],
    queryFn: () => twinTraceability.lotOutcome(lotId as string),
    enabled: live && !!lotId,
    staleTime: 60_000,
  });
}

export function useHeatLotsQuery(heatId: string | undefined) {
  const live = useLiveEnabled();
  return useQuery({
    queryKey: ["twin-heat-lots", heatId],
    queryFn: () => twinTraceability.heatLots(heatId as string),
    enabled: live && !!heatId,
    staleTime: 60_000,
  });
}
